import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { SkipForward, Circle, Eye, RotateCcw, Wifi } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Operator-Fernsteuerung fürs KinoIntro — mobiles Panel per QR.
 * Sendet Kommandos an /api/intro-remote/{showtimeId}/cmd, der Browser-Tab
 * mit dem laufenden Intro polled diese und wendet sie an.
 */
export default function IntroRemote() {
  const { showtimeId } = useParams();
  const [lastCmd, setLastCmd] = useState(null);
  const [sending, setSending] = useState(false);
  const [pulse, setPulse] = useState(false);
  const [showtime, setShowtime] = useState(null);

  useEffect(() => {
    axios.get(`${API}/intro/${showtimeId}`).then((r) => setShowtime(r.data?.showtime)).catch(() => {});
  }, [showtimeId]);

  const send = async (cmd) => {
    if (sending) return;
    setSending(true);
    setPulse(true);
    try {
      const { data } = await axios.post(`${API}/intro-remote/${showtimeId}/cmd`, { cmd });
      setLastCmd({ cmd: data.cmd, seq: data.seq, at: Date.now() });
    } catch { /* ignore */ }
    finally {
      setSending(false);
      setTimeout(() => setPulse(false), 400);
    }
  };

  const movie = showtime?.movie;

  return (
    <div className="min-h-screen bg-black text-[#F5F0E6] flex flex-col select-none" data-testid="intro-remote">
      {/* Kopf mit Film-Info */}
      <header className="px-5 pt-8 pb-4 border-b border-[#1A1A1A]">
        <div className="flex items-center gap-2 text-[9px] uppercase tracking-[0.4em] text-[#D4AF37]">
          <Wifi size={11} /> Operator-Fernsteuerung
          <span className={`ml-auto inline-block w-2 h-2 rounded-full transitions-safe ${pulse ? "bg-[#D4AF37]" : "bg-[#575148]"}`} />
        </div>
        <div className="mt-3 flex gap-3 items-start">
          {movie?.poster_url && (
            <img src={movie.poster_url} alt="" className="w-14 h-20 object-cover rounded shadow"
              onError={(e) => { e.currentTarget.style.display = "none"; }} />
          )}
          <div className="min-w-0">
            <div className="font-heading text-2xl leading-tight truncate">{movie?.title_de || movie?.title_original || "Vorstellung"}</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398] mt-1">
              {showtime?.date} · {showtime?.start_time} · {showtime?.hall}
            </div>
          </div>
        </div>
      </header>

      {/* Kommando-Buttons */}
      <main className="flex-1 flex flex-col gap-3 p-5">
        <RemoteBtn
          testid="remote-skip"
          icon={<SkipForward size={26} />}
          label="Skip"
          hint="Aktuellen Schritt überspringen"
          onClick={() => send("skip")}
          color="#F5F0E6"
        />
        <RemoteBtn
          testid="remote-blackout"
          icon={<Circle size={26} fill="currentColor" />}
          label="Blackout"
          hint="Direkt zum Vorhang · Bildschirm schwarz"
          onClick={() => send("blackout")}
          color="#D4AF37"
        />
        <RemoteBtn
          testid="remote-reveal"
          icon={<Eye size={26} />}
          label="Enthüllen"
          hint="Vorhang komplett schwarz — Apple TV übernimmt"
          onClick={() => send("reveal")}
          color="#EEDC82"
        />
        <RemoteBtn
          testid="remote-restart"
          icon={<RotateCcw size={22} />}
          label="Neu starten"
          hint="Intro von Anfang"
          onClick={() => send("restart")}
          color="#A8A398"
          small
        />
      </main>

      {/* Letztes Kommando */}
      <footer className="px-5 py-4 border-t border-[#1A1A1A] text-[10px] uppercase tracking-[0.3em] text-[#575148]">
        {lastCmd ? (
          <div className="flex justify-between">
            <span>Zuletzt: <span className="text-[#D4AF37]">{lastCmd.cmd}</span></span>
            <span>#{lastCmd.seq}</span>
          </div>
        ) : (
          <div>Bereit — tippe ein Kommando</div>
        )}
      </footer>
    </div>
  );
}

function RemoteBtn({ icon, label, hint, onClick, color = "#F5F0E6", testid, small = false }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`w-full flex items-center gap-4 ${small ? "p-4" : "p-5"} rounded-lg border border-[#2A2A2A] bg-[#0A0A0A] active:bg-[#151515] active:scale-[0.98] transitions-safe text-left`}
      style={{ borderColor: `${color}33` }}
    >
      <span style={{ color }} className="shrink-0">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="font-heading text-xl md:text-2xl" style={{ color }}>{label}</div>
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#A8A398] mt-0.5">{hint}</div>
      </div>
    </button>
  );
}
